import DatePicker from "antd/lib/date-picker/generatePicker";
import thTH from "antd/locale/th_TH";
import momentGenerateConfig from "rc-picker/lib/generate/moment";
import "antd/lib/date-picker/style";
import moment from "moment";

const TH_DATEPICKER_LOCALE = thTH.DatePicker;

const BE_OFFSET = 543;

const YEAR_TOKEN_REGEX = /\[[^\]]*\]|(Y+)/g;

const YEAR_SENTINEL_RAW = "__BE_YEAR__";
const YEAR_SENTINEL = `[${YEAR_SENTINEL_RAW}]`;

const withYearSentinel = (format) =>
  format.replace(YEAR_TOKEN_REGEX, (match, yearRun) =>
    yearRun ? YEAR_SENTINEL : match
  );

const hasYearToken = (format) => {
  YEAR_TOKEN_REGEX.lastIndex = 0;
  let m;
  while ((m = YEAR_TOKEN_REGEX.exec(format))) {
    if (m[1]) return true;
  }
  return false;
};

const isDayjsLike = (value) =>
  !!value &&
  typeof value === "object" &&
  typeof value.format === "function" &&
  typeof value.toDate === "function" &&
  typeof value.isValid === "function" &&
  typeof value._isAMomentObject === "undefined";

const toSafeMoment = (value) => {
  if (value === null || value === undefined || value === "") return value;
  if (moment.isMoment(value)) return value.clone();
  if (isDayjsLike(value)) {
    return moment(value.toDate());
  }
  return moment(value);
};

const toSafeMomentRange = (value) =>
  Array.isArray(value) ? value.map(toSafeMoment) : value;

const normalizeMomentLocale = (locale) =>
  typeof locale === "string" ? locale.toLowerCase().replace(/_/g, "-") : locale;

const withNormalizedLocale = (fn) =>
  typeof fn === "function"
    ? (locale, ...args) => fn(normalizeMomentLocale(locale), ...args)
    : fn;

const localeWithNormalizedName = Object.fromEntries(
  Object.entries(momentGenerateConfig.locale).map(([key, fn]) => [
    key,
    withNormalizedLocale(fn),
  ])
);

const buddhistGenerateConfig = {
  ...momentGenerateConfig,
  getYear: (date) => momentGenerateConfig.getYear(date) + BE_OFFSET,
  setYear: (date, year) => momentGenerateConfig.setYear(date, year - BE_OFFSET),
  locale: {
    ...localeWithNormalizedName,
    format: (locale, date, format) => {
      const normLocale = normalizeMomentLocale(locale);
      if (!hasYearToken(format)) {
        return momentGenerateConfig.locale.format(normLocale, date, format);
      }
      const beYear = momentGenerateConfig.getYear(date) + BE_OFFSET;
      const sentinelFormat = withYearSentinel(format);
      const formatted = momentGenerateConfig.locale.format(
        normLocale,
        date,
        sentinelFormat
      );
      return formatted.split(YEAR_SENTINEL_RAW).join(String(beYear));
    },
    parse: (locale, text, formats) => {
      const civilText = text.replace(/\b(2[4-9]\d{2}|[3-9]\d{3})\b/, (m) =>
        String(Number(m) - BE_OFFSET)
      );
      return momentGenerateConfig.locale.parse(
        normalizeMomentLocale(locale),
        civilText,
        formats
      );
    },
  },
};

const ThaiDatePickerBase = DatePicker(buddhistGenerateConfig);
const CivilDatePickerBase = DatePicker(momentGenerateConfig);

const BUDDHIST_DATE_FORMAT = "DD/MM/YYYY";
const CIVIL_DATE_FORMAT = "DD/MM/YYYY";

const toCivilDateString = (date) =>
  date && typeof date.format === "function"
    ? date.format(CIVIL_DATE_FORMAT)
    : date;

const wrapSingleOnChange = (onChange) => {
  if (!onChange) return onChange;
  return (date) => {
    onChange(date, toCivilDateString(date));
  };
};

const wrapRangeOnChange = (onChange) => {
  if (!onChange) return onChange;
  return (dates, dateStrings) => {
    const civilDateStrings = Array.isArray(dates)
      ? dates.map(toCivilDateString)
      : dateStrings;
    onChange(dates, civilDateStrings);
  };
};

const ThaiDatePicker = ({ format, onChange, value, defaultValue, ...rest }) => {
  const safeValue = toSafeMoment(value);
  const safeDefaultValue = toSafeMoment(defaultValue);
  if (format) {
    return (
      <CivilDatePickerBase
        locale={TH_DATEPICKER_LOCALE}
        {...rest}
        value={safeValue}
        defaultValue={safeDefaultValue}
        format={format}
        onChange={onChange}
      />
    );
  }
  return (
    <ThaiDatePickerBase
      locale={TH_DATEPICKER_LOCALE}
      {...rest}
      value={safeValue}
      defaultValue={safeDefaultValue}
      format={BUDDHIST_DATE_FORMAT}
      onChange={wrapSingleOnChange(onChange)}
    />
  );
};

const { RangePicker: ThaiRangePickerBase } = ThaiDatePickerBase;
const { RangePicker: CivilRangePickerBase } = CivilDatePickerBase;

const ThaiRangePicker = ({ format, onChange, value, defaultValue, ...rest }) => {
  const safeValue = toSafeMomentRange(value);
  const safeDefaultValue = toSafeMomentRange(defaultValue);
  if (format) {
    return (
      <CivilRangePickerBase
        locale={TH_DATEPICKER_LOCALE}
        {...rest}
        value={safeValue}
        defaultValue={safeDefaultValue}
        format={format}
        onChange={onChange}
      />
    );
  }
  return (
    <ThaiRangePickerBase
      locale={TH_DATEPICKER_LOCALE}
      {...rest}
      value={safeValue}
      defaultValue={safeDefaultValue}
      format={BUDDHIST_DATE_FORMAT}
      onChange={wrapRangeOnChange(onChange)}
    />
  );
};

export { ThaiDatePicker, ThaiRangePicker };
